import GemstoneGenericIcon from "../assets/images/icons/gemstone-generic.svg";
import { getGemTypeShapeIcon } from "./gemTypeShapeIcons";

// The API's ImageFileName/BigImageFileName fields are only ever populated two ways:
// a dealer-uploaded photo of the actual stone, or — when none exists — a diamond
// *shape* icon (round/cushion/pear/...), served under two different naming schemes
// depending on which field: the small grid icon is "Apps/Images/p1.png".."p10.png",
// the big detail-page one is "Apps/Images/BigImages/{shape}.jpg" (both shared across
// every builder — Diamond/Stud/Pendant/Ring/GemStone). Rendering either as if it were
// a photo of the stone is misleading (e.g. a Ruby gets a "cushion.jpg" diamond icon),
// so match on the fixed filename set itself rather than one folder, and treat a hit
// as "no real photo yet" instead of showing it.
const SHAPE_FALLBACK_FILENAMES = new Set([
  "p1.png", "p2.png", "p3.png", "p4.png", "p5.png",
  "p6.png", "p7.png", "p8.png", "p9.png", "p10.png",
  "round.jpg", "radiant.jpg", "princess.jpg", "pear.jpg", "oval.jpg",
  "marquise.jpg", "heart.jpg", "emerald.jpg", "cushion.jpg", "asscher_1.jpg",
  "trilliant.jpg", "antique.jpg", "baguette.jpg", "briolette.jpg", "bullet.jpg",
  "european.jpg", "halfmoon.jpg", "kite.jpg", "lozenge.jpg", "oldminer.jpg",
  "parallelogram.jpg", "shield.jpg", "star.jpg", "trapezoid.jpg", "triangle.jpg",
  "other.jpg",
]);

const getFilename = (url) => {
  if (typeof url !== "string" || !url) return "";
  const withoutQuery = url.split(/[?#]/)[0];
  return withoutQuery.split("/").pop().toLowerCase();
};

export const isShapeFallbackImage = (url) =>
  SHAPE_FALLBACK_FILENAMES.has(getFilename(url));

// Priority: a genuine dealer-uploaded photo of the stone > our local colour-matched
// gem-type+shape icon (bug #1807/#1808 stand-in, pending real photography in blob
// storage) > the neutral generic icon for any gem type we don't have a swatch for yet.
export const resolveGemPhoto = (url, gemTypeName, shapeName) => {
  if (url && !isShapeFallbackImage(url)) return url;
  return getGemTypeShapeIcon(gemTypeName, shapeName) || GemstoneGenericIcon;
};

// We only render one photographed colour per gem-type+shape (e.g. Sapphire is
// always the blue reference photo), but the same gem type spans many real
// txtColor values (a Sapphire row can be PINK, YELLOW, ...). Generating a real
// photo per gem-type x shape x colour isn't feasible, so retint the existing
// photo instead: grayscale+sepia first to normalize away its original hue to a
// known base, then hue-rotate to the target colour — this works consistently
// regardless of what colour the source photo actually was.
const COLOR_HUE_ROTATIONS = {
  RED: -20,
  ORANGE: 5,
  YELLOW: 30,
  BROWN: -10,
  GREEN: 95,
  TEAL: 150,
  BLUE: 180,
  VIOLET: 220,
  PURPLE: 250,
  PINK: 285,
};

export const getColorTintFilter = (txtColor) => {
  if (!txtColor) return "";
  const color = String(txtColor).trim().toUpperCase();
  if (color === "WHITE" || color === "GRAY" || color === "GREY") {
    return "grayscale(1) saturate(0) brightness(1.25)";
  }
  if (color === "BLACK") {
    return "grayscale(1) saturate(0) brightness(0.4)";
  }
  if (color === "BI COLOR" || color === "MULTI COLOR") {
    return "";
  }
  const hue = COLOR_HUE_ROTATIONS[color];
  if (hue === undefined) return "";
  return `grayscale(1) sepia(1) hue-rotate(${hue}deg) saturate(4.5) brightness(0.95)`;
};

export { GemstoneGenericIcon };
